<template>
  <Component
    :is="buttonOrNuxtLink"
    v-bind="{
      ...$attrs,
      ...(to ? { to, target: target || ($useIsExternalUrl(to) ? '_blank' : '_self') } : {}),
    }"
    :download="download"
    :class="variantStyle"
  >
    <!-- for download to work from s3-bucket, we need to set "Content-Disposition: attachment;" https://stackoverflow.com/a/50152327 -->
    <span v-if="$slots.icon" :class="iconStyle">
      <slot name="icon" />
    </span>
    <span v-if="$slots.default" class="relative">
      <slot />
    </span>

    <!-- SPINNER ICON -->
    <div class="transition-[width]" :class="isLoading ? 'w-7' : 'w-0'">
      <Transition name="fade">
        <div v-if="isLoading">
          <IconSpin class="ml-4 size-[1.25em] animate-spin" />
        </div>
      </Transition>
    </div>
  </Component>
</template>

<script setup lang="ts">
const slots = useSlots()

const props = withDefaults(defineProps<{
  to?: string | object
  target?: string
  variant?: string
  inverted?: boolean
  download?: string
  isLoading?: boolean
}>(), {
  variant: 'primary',
  inverted: false,
  isLoading: false,
})

const variantStyle = computed(() => {
  const classes = [
    `
      relative flex items-center justify-center rounded-100 py-3 font-semibold transition duration-200 ease-in
      typo-subtitle
      group-hover-focus:duration-300 group-hover-focus:ease-out
      hover-focus:duration-300 hover-focus:ease-out
      print:hidden
    `,
  ]

  if (slots.default) {
    classes.push('min-w-40 px-8 w-fit')
  }

  if (slots.default && slots.icon) {
    classes.push('px-3 gap-4')
  }

  if (!slots.default && slots.icon) {
    classes.push('size-10')
  }

  if (props.variant === 'primary') {
    if (props.inverted) {
      classes.push(`
          bg-white text-primary-900
          hover-focus:opacity-75 group-hover-focus:opacity-75
          active:!opacity-100
        `)
    } else {
      classes.push(`
          bg-primary-900 text-white
          before:bg-gradient-02 before:inset-0 before:absolute before:opacity-0
          before:transition before:duration-200 before:ease-in
          hover-focus:before:opacity-75 hover-focus:before:duration-300 hover-focus:before:ease-out
          group-hover-focus:before:opacity-75 group-hover-focus:before:duration-300 group-hover-focus:before:ease-out
          active:before:!opacity-0
        `)
    }

    classes.push(`
        disabled:bg-grey-200 disabled:text-grey-300 disabled:pointer-events-none
      `)
  }

  if (props.variant === 'secondary') {
    if (props.inverted) {
      classes.push(`
          text-white
          hover-focus:opacity-75
          group-hover-focus:opacity-75
          active:!opacity-100
        `)
      if (slots.default) {
        classes.push(`
            border border-2 border-white
          `)
      }
    } else {
      if (slots.default) {
        classes.push(`
            text-primary-900
            border border-2 border-primary-900
            hover-focus:border-tertiary-900 hover-focus:text-tertiary-900
            group-hover-focus:border-tertiary-900 group-hover-focus:text-tertiary-900
            active:!text-primary-900 active:!border-primary-900
          `)
      }
      if (!slots.default && slots.icon) {
        classes.push(`
            text-black
            hover-focus:text-primary-900
            group-hover-focus:text-primary-900
            active:!text-black
          `)
      }
    }

    classes.push(`
        disabled:border-grey-300 disabled:text-grey-300 disabled:pointer-events-none
      `)
  }

  return classes
})

const iconStyle = computed(() => {
  const classes = ['relative shrink-0']

  return classes
})

// determine whether to load NuxtLink or button
const buttonOrNuxtLink = computed(() => {
  if (props.to) {
    return resolveComponent('NuxtLink')
  }

  return 'button'
})
</script>
